export const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

export const blobToDataUrl = (blob) => readFileAsDataUrl(blob);

export const formatRecordingTime = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

export const getSupportedAudioMimeType = () => {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];

  return candidates.find((mimeType) => MediaRecorder.isTypeSupported?.(mimeType)) || '';
};

export const downscaleImageFile = async (file, options = {}) => {
  const { maxSide = 1280, quality = 0.84 } = options;
  const sourceDataUrl = await readFileAsDataUrl(file);

  if (!file.type.startsWith('image/')) {
    return {
      dataUrl: sourceDataUrl,
      mimeType: file.type || 'image/jpeg',
      width: null,
      height: null,
    };
  }

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = sourceDataUrl;
  });

  const largestSide = Math.max(image.width, image.height) || 1;
  const scale = Math.min(1, maxSide / largestSide);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, width, height);

  return {
    dataUrl: canvas.toDataURL('image/jpeg', quality),
    mimeType: 'image/jpeg',
    width,
    height,
  };
};

// Best-effort probe of an in-memory video data URL. Returns width, height and
// duration in seconds so the recipient can render a nicely sized inline
// player. Never rejects — falls back to nulls if the browser can't decode.
export const readVideoMetadata = (dataUrl) => new Promise((resolve) => {
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  const finalize = (payload) => {
    video.removeAttribute('src');
    video.load?.();
    resolve(payload);
  };
  video.onloadedmetadata = () => finalize({
    width: video.videoWidth || null,
    height: video.videoHeight || null,
    duration: Number.isFinite(video.duration) ? Math.round(video.duration) : null,
  });
  video.onerror = () => finalize({ width: null, height: null, duration: null });
  video.src = dataUrl;
});